import { useState } from 'react';
import { FetchPolicy, DOCUMENT_REFERENCE_TYPE } from 'utils/enum/Core';
import StringUtils from 'lib/StringUtils';
import PDFUtils from 'utils/PDFUtils';
import ImagesUtils from 'utils/ImagesUtils';
import GeneralUtils from 'utils/GeneralUtils';
import { useApolloClient } from '@apollo/client';
import DocumentsQuery from 'services/graphQL/query/DocumentsQuery';

export default function usePreview() {
    const client = useApolloClient();
    const [state, setState] = useState({
        previewTitle: '',
        previewExt: null,
        previewURL: null,
        isProcessingFile: false,
        isDialogOpen: false,
    });

    const onClosePreview = () => {
        setState((prevState) => ({
            ...prevState,
            previewTitle: '',
            previewExt: null,
            previewURL: null,
            isProcessingFile: false,
            isDialogOpen: false,
        }));
    };

    const onPreviewDocument = async (record, isDownload = false) => {
        const {
            documentName,
            documentUrl,
        } = record;

        setState((prevState) => ({
            ...prevState,
            isProcessingFile: true,
            isDialogOpen: !isDownload,
        }));

        const path = documentUrl;
        const ext = path.substring(path.lastIndexOf('.') + 1)?.toLowerCase();

        let localUrl = null;
        if (record.isSecureUrl) {
            const isSecured = StringUtils.isSecureURL(documentUrl);
            localUrl = isSecured ? documentUrl : documentUrl.replace('http://', 'https://');
        } else {
            const { data } = await client.query({
                query: DocumentsQuery.GET_DOCUMENT_SECURE_URL,
                variables: {
                    path,
                },
                fetchPolicy: FetchPolicy.NO_CACHE,
            });

            const [name] = Object.keys(data || {});
            if (!name) return;

            const secureURL = data[name];
            if (!secureURL) {
                setState((prevState) => ({
                    ...prevState,
                    isProcessingFile: false,
                }));

                return;
            }

            if (ext === 'pdf') {
                localUrl = await PDFUtils.convertPDFToBlobURL(secureURL);
            } else {
                localUrl = await ImagesUtils.convertImageToBlobURL(secureURL, 'image/jpeg');
            }
        }

        if (isDownload) {
            const {
                module,
                lotName,
                referenceId,
                referenceType,
                documentType,
            } = record;

            const currentReferenceId = referenceId > 0 ? `_${referenceId}` : '';
            const currentLotname = !StringUtils.isEmpty(lotName) ? `_${lotName}` : '';
            const subModule = [
                DOCUMENT_REFERENCE_TYPE.BILL,
                DOCUMENT_REFERENCE_TYPE.DEPOSIT,
                DOCUMENT_REFERENCE_TYPE.CHECK,
                DOCUMENT_REFERENCE_TYPE.VENDOR,
            ].includes(referenceType) ? `_${referenceType}` : '';
            const downloadDocumentName = `${module}${subModule}${currentReferenceId}${currentLotname}_${documentType}_${documentName}`;
            GeneralUtils.downloadFile(localUrl, downloadDocumentName.replace(/\s/g, '_').toUpperCase());
            setState((prevState) => ({
                ...prevState,
                isProcessingFile: false,
            }));

            return;
        }

        setState((prevState) => ({
            ...prevState,
            previewTitle: `| ${documentName}`,
            previewExt: ext,
            previewURL: localUrl,
            isProcessingFile: false,
        }));
    };

    return {
        state,
        onPreviewDocument,
        onClosePreview,
    };
}
